import type {CpcBuyFilters} from 'modules/Cpc/Buy/types';

import {InfoCircleOutlined} from '@ant-design/icons';
import {Divider} from '@ds/components';
import CpcInfoRow from 'modules/Cpc/components/common/CpcInfoRow/CpcInfoRow';
import {useEffect, useState} from 'react';
import {useAppSelector} from 'store';
import {numberFormat} from 'utils/Helper';

import ClickSelector from './components/ClickSelector/ClickSelector';
import PriceRangeSlider from './components/PriceRangeSlider/PriceRangeSlider';
import SuccessLegend from './components/SuccessLegend/SuccessLegend';

type ClickRateStepProps = {
  filters: CpcBuyFilters;
  setFilters: (filters: CpcBuyFilters) => void;
};

const PRICE_SEGMENTS = 50;

const ClickRateStep: React.FC<ClickRateStepProps> = ({filters, setFilters}) => {
  const {preorderCPC} = useAppSelector((state) => state.cpc);
  const [clickValue, setClickValue] = useState<number | string | null>(filters.click ?? null);
  const defaultPrice =
    filters.price ?? (preorderCPC.max_price + preorderCPC.min_price) / 2 + PRICE_SEGMENTS * 4;
  const [price, setPrice] = useState(defaultPrice);

  useEffect(() => {
    setFilters({...filters, price});
    // The URL receives its initial price once; subsequent changes are handled with the input events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickChange = (value: number | string) => {
    setClickValue(value);
    setFilters({...filters, click: value, price});
  };

  const handlePriceChange = (value: number) => {
    setPrice(value);
    setFilters({...filters, click: clickValue ?? undefined, price: value});
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col justify-between">
      <div>
        <ClickSelector
          clickValue={clickValue}
          setClickValue={handleClickChange}
          clickBuckets={preorderCPC.click_buckets}
        />
        <Divider className="mx-12.5! w-auto! bg-gray-03" />
        {preorderCPC.min_price && preorderCPC.max_price ? (
          <section className="flex flex-col gap-2.5 p-4">
            <h2 className="text-body-sm text-neutral-fg">پیشنهاد شما برای هر کلیک</h2>
            <PriceRangeSlider
              min={preorderCPC.min_price}
              max={preorderCPC.max_price}
              value={price}
              onChange={handlePriceChange}
              segments={PRICE_SEGMENTS}
            />
          </section>
        ) : null}
        <SuccessLegend />
        <div className="mx-2.5 mt-3 flex min-h-25 items-center gap-2.5 rounded-xl bg-yellow-soft p-3 text-justify text-body-sm text-gray-08">
          <InfoCircleOutlined className="shrink-0 text-body-lg text-yellow-dark-03" aria-hidden />
          <p>
            هرچه مبلغ را بیشتر کنی، همان تعداد کلیک را سریع‌تر و با کیفیت بهتر می‌گیری و می‌توانی
            زودتر کمپین جدید بزنی.
          </p>
        </div>
      </div>

      {filters.price && filters.click ? (
        <CpcInfoRow
          title="هزینه کل تخمین"
          value={`${numberFormat(Number(filters.price) * Number(filters.click))} تومان`}
        />
      ) : null}
    </div>
  );
};

export default ClickRateStep;
